import React from 'react';
import HighlightedText from 'components/HighlightedText';
import { formatEpochDate } from 'utilities/dateHelpers';
// Existing legacy invoice helpers (JS) reused per the redesign plan (AD-1).
import getInvoiceCostVisibility from 'screens/Invoices/helpers/getInvoiceCostVisibility';
import TagsRow from 'screens/Applications&WorkOrders/Components/RowItem/Components/TagsRow';
import {
  SUBTITLE_COLOR,
  SELECTION_COLOR
} from 'screens/Applications&WorkOrders/Components/Constants';
import { InvoiceSummary } from '../types';

export interface InvoiceRowItemProps {
  invoice: InvoiceSummary;
  isSelected?: boolean;
  searchTerm?: string;
  /** Member role used to mask cost (`Admin` | `Full control` | `View Only`). */
  memberRole?: string;
  /** Member financial access used to mask cost (`WRITE` | `READ` | `none`). */
  memberFinancialAccess?: string;
}

const SEARCH_HIGHLIGHT_COLOR = '#C6ECFF';
const SEARCH_HIGHLIGHT_SELECTED_COLOR = '#97C7ED';
const NOT_AVAILABLE = 'N/A';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children
}) => (
  <div className="shrink-0">
    <div className="text-xs font-normal" style={{ color: SUBTITLE_COLOR }}>
      {label}
    </div>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

/**
 * List-row content for one invoice. Renders the invoice name (with search
 * highlight), a compact row of the scannable summary fields (Invoice Date, Due
 * Date, Total Cost — masked for `financialAccess === 'none'`), then the tag
 * chips below. Mirrors the Applications/Work Orders `RowItem`, which keeps the
 * field row short so it never clips in the narrow list column; the secondary
 * fields (product count, crop seasons) live in the detail panel.
 */
const InvoiceRowItem: React.FC<InvoiceRowItemProps> = ({
  invoice,
  isSelected,
  searchTerm = '',
  memberRole,
  memberFinancialAccess
}) => {
  const costDisplay = getInvoiceCostVisibility({
    cost: invoice.totalCost,
    memberRole,
    memberFinancialAccess
  });

  const flags = invoice.flags ?? [];

  return (
    <div className="invoice-item-content w-full flex flex-col justify-center items-start relative">
      {isSelected && (
        <div
          style={{
            width: '4px',
            position: 'absolute',
            top: '-4px',
            bottom: '-4px',
            left: '-16px',
            borderRadius: '0 4px 4px 0',
            background: SELECTION_COLOR
          }}
        />
      )}

      <div className="invoice-name text-left text-base font-normal text-gray-900 w-full flex items-center justify-between">
        <HighlightedText
          text={invoice.title}
          searchTerm={searchTerm}
          isSelected={isSelected}
          highlightColor={SEARCH_HIGHLIGHT_COLOR}
          selectedHighlightColor={SEARCH_HIGHLIGHT_SELECTED_COLOR}
          className="truncate flex-1 block"
          style={{ lineHeight: '24px', marginBottom: '4px' }}
          highlightPadding="0"
        />
      </div>

      <div className="invoice-details flex items-end w-full gap-4">
        <Field label="Invoice Date">
          {invoice.invoiceDateEpoch
            ? formatEpochDate(invoice.invoiceDateEpoch)
            : NOT_AVAILABLE}
        </Field>
        <Field label="Total Cost">{costDisplay}</Field>
      </div>

      {flags.length > 0 && (
        <div style={{ marginTop: '8px', width: '100%' }}>
          <TagsRow tagIds={flags.map(flag => flag.id)} allTags={flags} />
        </div>
      )}
    </div>
  );
};

export default InvoiceRowItem;
